import { API_ENDPOINTS, getApiUrl } from "../config";
import { BillerSearchInfo } from "../view-models/BillerSearchInfo";
import { ConsumerLookupInfo } from "../view-models/ConsumerLookupInfo";
import { FieldsForProductInfo } from "../view-models/FieldsForProductInfo";
import { buildBffRequestHeaders } from "./bffRequestHeaders";
import { parseBffResponseText } from "../helper/sendValidationHelper";

function getValidateBillPayErrorMessageFromResponse(
  responseBody: unknown,
  fallbackMessage = "Validation failed.",
): string {
  if (!responseBody || typeof responseBody !== "object") {
    return fallbackMessage;
  }
  const w = responseBody as Record<string, unknown>;
  let data: any = w.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = null;
    }
  }
  if (data && typeof data === "object") {
    const dataMessage = String(
      (data as any).message ?? (data as any).Message ?? "",
    ).trim();
    if (dataMessage) return dataMessage;

    const errDetail = (data as any).ErrorDetail ?? (data as any).errorDetail;
    const detail = Array.isArray(errDetail) ? errDetail[0] : errDetail;
    const errString = String(
      detail?.ErrorString ?? detail?.errorString ?? "",
    ).trim();
    if (errString) return errString;
  }
  return fallbackMessage;
}


export const consumerLookup = async (
    phoneNumber: string,
    agentId: string,
    unitProfileId: string
  ): Promise<ConsumerLookupInfo> => {
      const payload = {
        AgentID: agentId,
        UnitProfileId: unitProfileId,
        ConsumerPhone: phoneNumber
      };
    
      const apiUrl = getApiUrl(API_ENDPOINTS.CONSUMER_LOOKUP_BILLPAY);
      console.log('Making API call to ConsumerLookup with payload:', payload);
      console.log('API URL:', apiUrl);
    
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: buildBffRequestHeaders(),
          body: JSON.stringify(payload),
        });
    
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
    
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Failed to lookup consumer: ${response.status} ${response.statusText}`);
        }
    
        const data = await response.json();
        console.log('API Response data:', data);
  
        // Some backends wrap real payload as JSON string in `data`
        // Example shape: { success: true, data: "{\"ErrorDetail\":{...}}", message: "..." }
        const possiblyWrapped = data as any;
        let parsed: any = possiblyWrapped;
        if (possiblyWrapped && typeof possiblyWrapped.data === 'string') {
          try {
            parsed = JSON.parse(possiblyWrapped.data);
          } catch {
            // Keep original if parse fails
            parsed = possiblyWrapped;
          }
        } else if (
          possiblyWrapped &&
          typeof possiblyWrapped.data === 'object' &&
          possiblyWrapped.data !== null &&
          !Array.isArray(possiblyWrapped.data)
        ) {
          parsed = possiblyWrapped.data;
        }
  
        // If ErrorDetail exists, check ErrorCode 616 and surface a structured error
        const errorCode = parsed?.ErrorDetail?.ErrorCode;
        if (errorCode) {
          const structured: any = {
            name: 'ConsumerLookupError',
            message: parsed?.ErrorDetail?.ErrorString || 'Consumer lookup failed',
            code: errorCode,
            offendingField: parsed?.ErrorDetail?.OffendingField,
            raw: parsed
          };
          if (String(errorCode) === '616') {
            throw structured;
          }
        }
  
        return parsed as ConsumerLookupInfo;
      } catch (error) {
        console.error('Fetch error:', error);
        
        // If it's a CORS error, provide helpful information
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
          throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
        }
        
        throw error;
      }
}

export const billerSearch = async (
    billerName: string,
    agentId: string,
    unitProfileId: string,
    searchType: string,
    receiveCode?: string
  ): Promise<BillerSearchInfo> => {
      const payload: any = {
        AgentID: agentId,
        UnitProfileId: unitProfileId,
        BillerName: billerName,
        searchType: searchType
      };
      if (receiveCode) {
        payload.ReceiveCode = receiveCode;
        payload.receiveCode = receiveCode;
      }
    
      const apiUrl = getApiUrl(API_ENDPOINTS.BILLER_SEARCH);
      console.log('Making API call to BillerSearch with payload:', payload);
      console.log('API URL:', apiUrl);
    
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: buildBffRequestHeaders(),
          body: JSON.stringify(payload),
        });
    
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
    
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Failed to lookup consumer: ${response.status} ${response.statusText}`);
        }
    
        const data = await response.json();
        console.log('API Response data:', data);
  
        // Some backends wrap real payload as JSON string in `data`
        // Example shape: { success: true, data: "{\"ErrorDetail\":{...}}", message: "..." }
        const possiblyWrapped = data as any;
        let parsed: any = possiblyWrapped;
        if (possiblyWrapped && typeof possiblyWrapped.data === 'string') {
          try {
            parsed = JSON.parse(possiblyWrapped.data);
          } catch {
            // Keep original if parse fails
            parsed = possiblyWrapped;
          }
        }
  
        // If ErrorDetail exists, check ErrorCode 616 and surface a structured error
        const errorCode = parsed?.ErrorDetail?.ErrorCode;
        if (errorCode) {
          const structured: any = {
            name: 'BillerSearchError',
            message: parsed?.ErrorDetail?.ErrorString || 'Biller search failed',
            code: errorCode,
            offendingField: parsed?.ErrorDetail?.OffendingField,
            raw: parsed
          };
          if (String(errorCode) === '616') {
            throw structured;
          }
        }
  
        return parsed as BillerSearchInfo;
      } catch (error) {
        console.error('Fetch error:', error);
        
        // If it's a CORS error, provide helpful information
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
          throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
        }
        
        throw error;
      }
}

export const postFeeLookup = async (feeLookupData: any) => {
  const url = getApiUrl(API_ENDPOINTS.BILLPAY_FEE_LOOKUP);
 
  
  try {
      const response = await fetch(url, {
          method: 'POST',
          headers: buildBffRequestHeaders(),
          body: JSON.stringify(feeLookupData)
      });
      
      if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
      }
      const text = await response.text();
      let data;
      try {
          data = JSON.parse(text);
          // Parse the nested data property if it's a JSON string
          let feeData = data;
          if (data.data && typeof data.data === 'string') {
              try {
                  feeData = JSON.parse(data.data);
                  console.log("Parsed nested data:", feeData);
              } catch (nestedParseError) {
                  console.error('Failed to parse nested data JSON:', nestedParseError);
                  throw new Error('API returned invalid nested JSON');
              }
          }
      } catch (parseError) {
          console.error('Failed to parse JSON:', parseError);
          throw new Error('API returned invalid JSON');
      }
      
      // Only treat as error when backend explicitly sets success=false.
      if (typeof (data as any)?.success === 'boolean' && (data as any).success === false) {
          throw new Error((data as any).error || 'Failed to perform fee lookup');
      }

      // Process fee infos to get unique currencies and exchange rates
      let feeData = data;
      if (data.data && typeof data.data === 'string') {
          try {
              feeData = JSON.parse(data.data);
          } catch (nestedParseError) {
              console.error('Failed to parse nested data JSON:', nestedParseError);
              throw new Error('API returned invalid nested JSON');
          }
      }

      if (feeData.FeeInfos && Array.isArray(feeData.FeeInfos)) {
          // Helper function to get receive currency (same logic as getReceiveCurrency method)
          const getReceiveCurrency = (fee: any): string => {
              if (fee.ReceiveAmounts?.ValidCurrencyIndicator) {
                  return fee.ValidReceiveCurrency || '';
              } else {
                  return fee.EstimatedReceiveCurrency || (fee.ValidReceiveCurrency || '');
              }
          };
          
          // Group by receive currency and get first fee info for each currency
          const uniqueFeeInfos = feeData.FeeInfos.reduce((acc: any[], fee: any) => {
              const receiveCurrency = getReceiveCurrency(fee);
              if (!acc.find((f: any) => getReceiveCurrency(f) === receiveCurrency)) {
                  acc.push(fee);
              }
              return acc;
          }, []);

          // Add exchange rate information. Pick NoN USD
          const exchangeRates = uniqueFeeInfos.map((fee: any) => ({
              receiveCurrency: getReceiveCurrency(fee),
              exchangeRate: fee.ValidExchangeRate || fee.EstimatedExchangeRate,
              isEstimated: !fee.ValidExchangeRate && fee.EstimatedExchangeRate,
              totalSendFees: Number(fee?.SendAmounts?.TotalSendFees ?? 0)
          })).filter((fee: any) => fee.receiveCurrency !== 'USD'); 

          data.exchangeRates = exchangeRates;
          console.log("Response fee lookup data exchangeRates:", data.exchangeRates);
      } else {
          console.log("No FeeInfos found or not an array");
      }
      return data;
  } catch (error) {
      console.error('Error performing fee lookup:', error);
      throw error;
  }
}

export const getFieldsForProductRequest = async (gffpData: any): Promise<FieldsForProductInfo> => {
  const url = getApiUrl(API_ENDPOINTS.GET_FIELDS_FOR_PRODUCT_FOR_BILLPAY);
  console.log('Service calling getFieldsForProductRequest URL:', url);
  console.log('GetFieldsForProductRequest payload:', gffpData);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(gffpData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const text = await response.text();
    let wrapper: any;
    try {
      wrapper = JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse JSON:', parseError);
      throw new Error('API returned invalid JSON');
    }

    if (wrapper && wrapper.success === false) {
      throw new Error(wrapper.error || 'Failed to load fields for product');
    }

    let payload: any = wrapper;
    if (wrapper && typeof wrapper.data === 'string') {
      try {
        payload = JSON.parse(wrapper.data);
      } catch (nestedParseError) {
        console.error('Failed to parse nested data JSON:', nestedParseError);
        throw new Error('API returned invalid nested JSON');
      }
    } else if (wrapper && typeof wrapper.data === 'object') {
      payload = wrapper.data;
    }

    // If backend embeds an error payload within a 200, surface it as an exception
    const embeddedError =
      payload?.ErrorDetail?.ErrorString ||
      payload?.ErrorDetail?.ErrorMessage ||
      payload?.message ||
      payload?.Message;
    if (embeddedError) {
      throw new Error(String(embeddedError));
    }

    // Normalize key casing from backend (often PascalCase) to our camelCase model
    const normalized: FieldsForProductInfo = {
      fqdoInfo: (payload as any).fqdoInfo ?? (payload as any).FqdoInfo,
      productFieldInfos: ((payload as any).productFieldInfos ?? (payload as any).ProductFieldInfos ?? []).map((f: any) => ({
        ...f,
        visibility: f.visibility ?? f.Visibility,
      })),
      dynamicFieldInfos: (payload as any).dynamicFieldInfos ?? (payload as any).DynamicFieldInfos,
    } as FieldsForProductInfo;

    return normalized;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

export const validateBillPay = async (data: any): Promise<any> => {
  const url = getApiUrl(API_ENDPOINTS.VALIDATE_BILLPAY);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildBffRequestHeaders(),
      body: JSON.stringify(data),
    });
    const text = await response.text();
    const wrapper = parseBffResponseText(text);

    if (wrapper && typeof wrapper === "object") {
      if (!response.ok || (wrapper as any).success === false) {
        throw new Error(
          getValidateBillPayErrorMessageFromResponse(
            wrapper,
            "Validation failed.",
          ),
        );
      }
      let payload: any = wrapper;
      if (typeof (wrapper as any).data === "string") {
        try {
          payload = JSON.parse((wrapper as any).data);
        } catch {
          payload = (wrapper as any).data;
        }
      } else if (
        typeof (wrapper as any).data === "object" &&
        (wrapper as any).data !== null
      ) {
        payload = (wrapper as any).data;
      }
      return payload;
    }

    if (!response.ok) {
      throw new Error(
        getValidateBillPayErrorMessageFromResponse(
          parseBffResponseText(text),
          `ValidateBillPay failed: ${response.status} ${response.statusText}`,
        ),
      );
    }
    return text;
  } catch (error) {
    console.error('Fetch error:', error);
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
    }
    throw error;
  }
}